import React, { useRef, useCallback, useEffect } from 'react';
import { pick } from 'ramda';
import { useJackpotsSelector } from 'js/store/hooks';
import { componentService } from 'js/services/component.service';

import * as hooks from './hooks';
import * as Types from './types';
import * as helpers from './helpers';

export const Jackpot: React.FC<Types.IJackpotProps> = ({
    code,
    interval = 100,
    className = '',
    enableHitTime = true
}) => {
    const animationFrame = useRef<number>(0);
    const jackpotRef = useRef<HTMLSpanElement>(null);
    const {
        jackpots, totalCasinoJackpot, jackpotStartTime
    } = useJackpotsSelector(pick(['jackpots', 'totalCasinoJackpot', 'jackpotStartTime']));
    const jackpot = code && jackpots[code] || !code && totalCasinoJackpot;
    const updateTickerValue = hooks.useUpdateTickerValue();

    const updateAnimation = useCallback((tickerData: Types.IJackpotTickerData) => {
        animationFrame.current && cancelAnimationFrame(animationFrame.current);

        if (!tickerData.isLimitReached || !tickerData.isHitTimeEnded) {
            animationFrame.current = requestAnimationFrame(() => updateAnimation(tickerData));
        }

        updateTickerValue({ enableHitTime, interval, jackpotRef, tickerData });
    }, [updateTickerValue, enableHitTime, interval]);

    useEffect(() => {
        if (jackpot) {
            updateAnimation(helpers.getInitialTickerData(jackpotStartTime, jackpot));
        }

        return () => cancelAnimationFrame(animationFrame.current);
    }, [jackpot, updateAnimation, jackpotStartTime]);

    if (!jackpot) {
        return null;
    }

    return (
        <span
            ref={jackpotRef}
            className={className}
            data-jackpot-code={code || ''}
        />
    );
};

/**
 * "Jackpot" (<hub2-jackpot code="jackpot_code"></hub2-jackpot>) is a regular component that shows a jackpot per code.
 * "Jackpots (<hub2-jackpots></hub2-jackpots>)" is a component that shows Total Casino Jackpot.
 */
componentService.registerWebContentComponents({ Jackpot, Jackpots: Jackpot });
